import {
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  Select,
  Skeleton,
  Switch,
  Tooltip,
  Typography
} from 'antd';
import { EvaluationWeightsVisualization } from './EvaluationWeightsVisualization';
import { FormItemCol } from 'components/professor_area/FormItemCol';
import { InfoCircleOutlined } from '@ant-design/icons';
import {
  PREVIEW_EVALUATION_ID,
  mergeEvaluationsWithPreview
} from './evaluationWeightsUtils';
import { useEffect, useMemo, useRef } from 'react';
import type {
  IEvaluationKind,
  IProfessorAreaEvaluationsList
} from 'interfaces/professor_area/evaluations';

/**
 * Shared "Parâmetros" step body for the professor-area create modals.
 *
 * Both the Avaliação (ModalCreateEvaluationStep2) and Atividade
 * (ModalCreateAssignmentStep3) flows render the exact same fields — access
 * period, kind, optional toggle, weight, passing score, attempts and the
 * weights preview. This component is the single source; the two steps are thin
 * wrappers that only pass `entity` (and, for the replacement flow, `isReplacement`).
 */
export type ParametersEntity = 'assessment' | 'assignment';

interface EntityCopy {
  defaultType: 'Assessment' | 'Assignment';
  accessTooltip: string;
  relativeDurationHelp: string;
  kindLabel: string;
  kindTooltip: string;
  kindOptions: { label: string; value: string }[];
  optionalLabel: string;
  optionalTooltip: string;
}

const ENTITY_COPY: Record<ParametersEntity, EntityCopy> = {
  assessment: {
    defaultType: 'Assessment',
    accessTooltip:
      'O período de acesso de uma avaliação deve estar entre a data de início e fim da turma',
    relativeDurationHelp: 'dias após o início da avaliação.',
    kindLabel: 'Tipo de avaliação',
    kindTooltip:
      'A prova final segue regras próprias no cálculo e não usa peso como as avaliações padrão. Deve ocorrer após as demais avaliações.',
    kindOptions: [
      { label: 'Avaliação padrão', value: 'regular' },
      { label: 'Prova final', value: 'final' }
    ],
    optionalLabel: 'Avaliação opcional',
    optionalTooltip:
      'Quando ativada, a avaliação não entra na média da turma (peso 0). O campo Peso fica desabilitado. Use para pesquisas, NPS ou atividades fora da nota.'
  },
  assignment: {
    defaultType: 'Assignment',
    accessTooltip:
      'O período de acesso de uma atividade deve estar entre a data de início e fim da turma',
    relativeDurationHelp: 'dias após o início da atividade.',
    kindLabel: 'Tipo de atividade',
    kindTooltip:
      'A atividade de recuperação segue regras próprias no cálculo e não usa peso como as atividades padrão. Deve ocorrer após as demais atividades.',
    kindOptions: [
      { label: 'Atividade padrão', value: 'regular' },
      { label: 'Atividade de recuperação', value: 'final' }
    ],
    optionalLabel: 'Atividade opcional',
    optionalTooltip:
      'Quando ativada, a atividade não entra na média da turma (peso 0). O campo Peso fica desabilitado. Use para atividades fora da nota.'
  }
};

interface EvaluationParametersFieldsProps {
  entity: ParametersEntity;
  classroomEvaluations: IProfessorAreaEvaluationsList[];
  evaluationsLoading: boolean;
  /** Quando true, esconde tipo (kind), peso e prévia — fluxo de substitutiva. */
  isReplacement?: boolean;
}

export const EvaluationParametersFields = ({
  entity,
  classroomEvaluations,
  evaluationsLoading,
  isReplacement
}: EvaluationParametersFieldsProps) => {
  const copy = ENTITY_COPY[entity];
  const form = Form.useFormInstance();
  const kind = Form.useWatch('kind');
  const accessType = Form.useWatch('access_type');
  const optionalEvaluation = Form.useWatch('optional_evaluation');
  const previewName = Form.useWatch('name');
  const previewWeight = Form.useWatch('weight');
  const evaluationType = Form.useWatch('evaluation_type');

  const effectiveKind = kind ?? 'regular';

  const previewEvaluationType: 'Assessment' | 'Assignment' =
    evaluationType === 'Assignment'
      ? 'Assignment'
      : evaluationType === 'Assessment'
      ? 'Assessment'
      : copy.defaultType;

  const optionalEvaluationPrev = useRef<boolean | undefined>();

  useEffect(() => {
    if (kind === 'final') {
      form.setFieldValue('optional_evaluation', false);
      optionalEvaluationPrev.current = false;
    }
  }, [kind, form]);

  useEffect(() => {
    if (kind === 'final' || optionalEvaluation === undefined) {
      return;
    }

    if (optionalEvaluationPrev.current === optionalEvaluation) {
      return;
    }
    const previous = optionalEvaluationPrev.current;
    optionalEvaluationPrev.current = optionalEvaluation;

    if (previous === undefined && optionalEvaluation === false) {
      return;
    }

    if (optionalEvaluation) {
      form.setFieldValue('weight', 0);
    } else {
      form.setFieldValue('weight', 1);
    }
  }, [optionalEvaluation, kind, form]);

  const distributionEvaluations = useMemo(
    () =>
      mergeEvaluationsWithPreview(
        classroomEvaluations,
        effectiveKind === 'regular'
          ? {
              name: typeof previewName === 'string' ? previewName : '',
              weight: Number(previewWeight ?? 0),
              kind: 'regular',
              evaluation_type: previewEvaluationType
            }
          : null
      ),
    [
      classroomEvaluations,
      effectiveKind,
      previewName,
      previewWeight,
      previewEvaluationType
    ]
  );

  return (
    <>
      <Typography.Title level={4} className="!mb-2">
        Parâmetros
      </Typography.Title>

      <Typography.Title className="align-middle gap-2 flex" level={5}>
        Período de acessos
        <Tooltip placement="rightBottom" title={copy.accessTooltip}>
          <InfoCircleOutlined />
        </Tooltip>
      </Typography.Title>

      {!isReplacement && (
        <Row>
          <Col span={24} style={{ marginBottom: 8 }}>
            <Form.Item
              name="access_type"
              initialValue="fixed"
              help={
                accessType === 'fixed' || !accessType
                  ? 'O acesso ocorre em uma data de início específica, independente da data de matrícula do aluno'
                  : 'O acesso ocorre a partir de um número de dias após a matrícula do aluno'
              }
              style={{ marginBottom: 0 }}
            >
              <Radio.Group buttonStyle="outline" className="mb-2">
                <Radio.Button value="fixed">Data fixa</Radio.Button>
                <Radio.Button value="relative">Data relativa</Radio.Button>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>
      )}

      {isReplacement && (
        <Form.Item name="access_type" initialValue="fixed" hidden noStyle>
          <Input type="hidden" />
        </Form.Item>
      )}

      {accessType === 'fixed' && (
        <>
          <FormItemCol
            label="Início"
            name="starts_at"
            cols={[7, 17]}
            gutter={[0, 0]}
            rules={[{ required: true, message: 'Informe a data de início' }]}
          >
            <DatePicker showTime format="DD/MM/YYYY HH:mm" />
          </FormItemCol>

          <FormItemCol
            label="Término"
            name="ends_at"
            cols={[7, 17]}
            gutter={[0, 0]}
            rules={[{ required: true, message: 'Informe a data de término' }]}
          >
            <DatePicker showTime format="DD/MM/YYYY HH:mm" />
          </FormItemCol>
        </>
      )}

      {accessType === 'relative' && (
        <>
          <FormItemCol
            label="Dias para início"
            name="start_after_days"
            rules={[{ required: true }]}
            cols={[7, 4, 10]}
            help="dias após a matrícula do aluno."
          >
            <InputNumber />
          </FormItemCol>
          <FormItemCol
            label="Duração (dias)"
            name="duration_days"
            rules={[{ required: true }]}
            cols={[7, 4, 10]}
            help={copy.relativeDurationHelp}
          >
            <InputNumber />
          </FormItemCol>
        </>
      )}

      {!isReplacement && (
        <KindAndWeightSection
          copy={copy}
          kind={kind}
          optionalEvaluation={optionalEvaluation}
        />
      )}

      {!isReplacement && <Divider />}

      {!isReplacement && (
        <FormItemCol
          cols={[7, 17]}
          name="passing_score"
          label="Nota mínima (0–100)"
          rules={[{ required: true, message: 'Campo obrigatório' }]}
        >
          <InputNumber
            min={0}
            max={100}
            className="max-w-[120px]"
            placeholder="0–100"
          />
        </FormItemCol>
      )}

      <FormItemCol
        cols={[7, 17]}
        name="max_attempts"
        label="Tentativas"
        rules={[{ required: true, message: 'Campo obrigatório' }]}
      >
        <InputNumber min={0} className="max-w-[120px]" placeholder="0" />
      </FormItemCol>

      {isReplacement && (
        <FormItemCol cols={[7, 17]} name="rule" label="Regra de liberação">
          <Select
            className="max-w-[360px]"
            options={[
              {
                value: null,
                label: 'Alunos que não foram aprovados ou perderam a avaliação'
              },
              {
                value: 'classroom_enrollment',
                label: 'Substitutiva especial'
              }
            ]}
          />
        </FormItemCol>
      )}

      {!isReplacement && (
        <WeightsPreview
          evaluationsLoading={evaluationsLoading}
          distributionEvaluations={distributionEvaluations}
          effectiveKind={effectiveKind}
          previewName={previewName}
          previewEvaluationType={previewEvaluationType}
        />
      )}
    </>
  );
};

interface WeightsPreviewProps {
  evaluationsLoading: boolean;
  distributionEvaluations: IProfessorAreaEvaluationsList[];
  effectiveKind: IEvaluationKind;
  previewName: unknown;
  previewEvaluationType: 'Assessment' | 'Assignment';
}

const WeightsPreview = ({
  evaluationsLoading,
  distributionEvaluations,
  effectiveKind,
  previewName,
  previewEvaluationType
}: WeightsPreviewProps) => {
  const finalPreview =
    effectiveKind === 'final'
      ? {
          name: typeof previewName === 'string' ? previewName : '',
          evaluation_type: previewEvaluationType
        }
      : undefined;

  return (
    <div className="mt-4 rounded-lg border border-neutral-200 bg-neutral-50/90 px-4 py-3">
      <Typography.Title level={5} className="!mb-3 !mt-0">
        Prévia dos pesos
      </Typography.Title>
      {evaluationsLoading ? (
        <Skeleton active paragraph={{ rows: 3 }} title={false} />
      ) : (
        <EvaluationWeightsVisualization
          evaluations={distributionEvaluations}
          highlightSegmentId={
            effectiveKind === 'regular' ? PREVIEW_EVALUATION_ID : undefined
          }
          hideIntro={effectiveKind === 'regular'}
          finalPreview={finalPreview}
        />
      )}
    </div>
  );
};

interface KindAndWeightSectionProps {
  copy: EntityCopy;
  kind: string | undefined;
  optionalEvaluation: boolean | undefined;
}

const KindAndWeightSection = ({
  copy,
  kind,
  optionalEvaluation
}: KindAndWeightSectionProps) => {
  const isFinal = kind === 'final';

  return (
    <>
      <Divider />

      <Form.Item>
        <Row gutter={[40, 16]}>
          <Col md={7}>
            <span className="inline-flex items-center gap-1">
              {copy.kindLabel}
              <Tooltip title={copy.kindTooltip} placement="top">
                <InfoCircleOutlined className="text-neutral-400" />
              </Tooltip>
            </span>
          </Col>
          <Col md={17}>
            <Form.Item
              noStyle
              name="kind"
              initialValue="regular"
              rules={[{ required: true, message: 'Campo obrigatório' }]}
            >
              <Radio.Group
                options={copy.kindOptions}
                optionType="button"
                buttonStyle="outline"
                size="middle"
              />
            </Form.Item>
          </Col>
        </Row>
      </Form.Item>

      <Divider />

      <Typography.Title level={5} className="!mb-3">
        Peso na média e nota
      </Typography.Title>

      {!isFinal && (
        <FormItemCol
          cols={[7, 17]}
          name="optional_evaluation"
          valuePropName="checked"
          initialValue={false}
          label={
            <span className="inline-flex items-center gap-1">
              {copy.optionalLabel}
              <Tooltip title={copy.optionalTooltip} placement="topLeft">
                <InfoCircleOutlined className="text-neutral-400" />
              </Tooltip>
            </span>
          }
        >
          <Switch />
        </FormItemCol>
      )}

      <FormItemCol
        cols={[7, 17]}
        name="weight"
        label="Peso na média"
        hidden={isFinal}
        initialValue={1}
        rules={[
          { required: !isFinal, message: 'Campo obrigatório' },
          {
            type: 'number',
            min: 0,
            max: 100,
            message: 'Informe um valor entre 0 e 100'
          }
        ]}
      >
        <InputNumber
          min={0}
          max={100}
          className="max-w-[120px]"
          placeholder="0–100"
          disabled={Boolean(optionalEvaluation)}
        />
      </FormItemCol>
    </>
  );
};
